// SPDX-License-Identifier: AGPL-3.0-only

interface ApiErrorPayload {
  readonly code?: unknown;
  readonly message?: unknown;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code: string;

  public constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

async function readResponsePayload(response: Response): Promise<unknown> {
  const responseText: string = await response.text();
  if (responseText === "") {
    return undefined;
  }

  try {
    return JSON.parse(responseText) as unknown;
  } catch (error: unknown) {
    if (!response.ok) {
      throw new ApiRequestError(
        response.status,
        "NON_JSON_ERROR_RESPONSE",
        `Request failed with status ${response.status}. The server returned a non-JSON error response.`,
      );
    }
    throw error;
  }
}

export async function requestJson(path: string, init?: RequestInit): Promise<unknown> {
  const headers: Headers = new Headers(init?.headers);
  if (init?.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response: Response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  const payload: unknown = await readResponsePayload(response);

  if (!response.ok) {
    const errorPayload: ApiErrorPayload =
      typeof payload === "object" && payload !== null ? (payload as ApiErrorPayload) : {};
    const code: string =
      typeof errorPayload.code === "string" ? errorPayload.code : "REQUEST_FAILED";
    const message: string =
      typeof errorPayload.message === "string"
        ? errorPayload.message
        : `Request failed with status ${response.status}`;

    throw new ApiRequestError(response.status, code, message);
  }

  return payload;
}
